import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    Link,
    MenuItem,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Rating } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HomeIcon from '@material-ui/icons/Home'
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import { ReactComponent as RibbonThumbGreenIcon } from '../../assets/icons/thumbs_ribbon-32-filled-big.svg'
import { ReactComponent as RibbonAirplaneDarkIcon } from '../../assets/icons/airplane_ribbon-32-filled-big.svg'
import { FloorplanPreview } from '../../components'
import { axiosInstance } from '../../helpers'
import {
    NVLVendorTier,
    NVL_TIER_LABELS,
    NVL_TIER_VOLUME_ROWS,
} from '../../main/containers/national-vendor-list/nvlVendorModel'
import {
    BidInviteTier,
    GeneratedRFPInsurance,
    GeneratedRFPMixGroup,
    GeneratedRFPPacket,
    GeneratedRFPServicePacket,
    GeneratedRFPVendor,
} from '../../models'
import { getServiceImageDetails } from '../../models/WorkOrder/services'
import { Routes } from '../../navigation/config/routes'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    onClose: () => void
    apartmentId?: number
    apartmentName?: string
    queueLabel?: string
    initialYear?: number
    onSent?: () => void
    dashboardPath?: string
}

interface ServiceDraft {
    included: boolean
    vendorIds: Record<number, boolean>
    includeCurrent: boolean
    insurance: GeneratedRFPInsurance | null
    uploading: boolean
    notes: string
}

const SERVICE_NOTES_MAX = 500

const emptyDraft = (): ServiceDraft => ({
    included: true,
    vendorIds: {},
    includeCurrent: true,
    insurance: null,
    uploading: false,
    notes: '',
})

const TIER_LABEL: Record<BidInviteTier, string> = {
    [BidInviteTier.CURRENT]: 'Current',
    [BidInviteTier.NEARBY]: 'Nearby',
    [BidInviteTier.WILL_TRAVEL]: 'Will travel',
}

const formatCompleteCount = (count: number) => {
    if (count >= 1000) {
        return `${Math.floor(count / 1000)}K+`
    }
    if (count >= 100) {
        return `${Math.floor(count / 100) * 100}+`
    }
    return String(count)
}

const tierColorFor = (tier: NVLVendorTier, theme: Theme) => {
    if (tier === 'silver') {
        return theme.palette.silver.main
    }
    if (tier === 'gold') {
        return theme.palette.gold.main
    }
    if (tier === 'blue') {
        return theme.palette.blue.main
    }
    if (tier === 'dark_blue') {
        return theme.palette.darkBlue.main
    }
    return theme.palette.primary.dark
}

const TierVolumeTooltip = () => (
    <div style={{ padding: 4 }}>
        <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 12 }}>
            Tiers by total work-order volume
        </div>
        <div style={{ fontSize: 11, marginBottom: 6, opacity: 0.9 }}>
            Based on all work orders (not only completed).
        </div>
        <table
            style={{
                borderCollapse: 'collapse',
                fontSize: 12,
                lineHeight: 1.4,
            }}
        >
            <thead>
                <tr>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '2px 10px 4px 0',
                            fontWeight: 600,
                        }}
                    >
                        Work orders
                    </th>
                    <th
                        style={{
                            textAlign: 'left',
                            padding: '2px 0 4px 0',
                            fontWeight: 600,
                        }}
                    >
                        Tier
                    </th>
                </tr>
            </thead>
            <tbody>
                {NVL_TIER_VOLUME_ROWS.map((row) => (
                    <tr key={row.tier}>
                        <td style={{ padding: '2px 10px 2px 0' }}>
                            {row.volume}
                        </td>
                        <td style={{ padding: '2px 0' }}>{row.tier}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
)

const mixGroupKey = (group: Pick<GeneratedRFPMixGroup, 'bed_count'>) =>
    `${group.bed_count ?? 'unknown'}`

const formatSqft = (row: {
    min_square_footage?: number | null
    max_square_footage?: number | null
}) => {
    if (row.min_square_footage && row.max_square_footage) {
        if (row.min_square_footage === row.max_square_footage) {
            return `${row.min_square_footage.toLocaleString()} sq ft`
        }
        return `${row.min_square_footage.toLocaleString()}–${row.max_square_footage.toLocaleString()} sq ft`
    }
    if (row.min_square_footage) {
        return `${row.min_square_footage.toLocaleString()} sq ft`
    }
    if (row.max_square_footage) {
        return `${row.max_square_footage.toLocaleString()} sq ft`
    }
    return null
}

const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '')

const formatClusterDate = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number)
    if (!year || !month || !day) {
        return iso
    }
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    })
}

const serviceVendors = (service: GeneratedRFPServicePacket) => [
    ...service.vendors.current,
    ...service.vendors.nearby,
    ...service.vendors.will_travel,
]

export const RequestRFPModal = (props: Props) => {
    const {
        open,
        onClose,
        apartmentId,
        apartmentName,
        queueLabel,
        initialYear,
        onSent,
        dashboardPath,
    } = props
    const theme = useTheme()
    const history = useHistory()

    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [packet, setPacket] = useState<GeneratedRFPPacket | null>(null)
    const [year, setYear] = useState<number | null>(null)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [deadline, setDeadline] = useState('')
    const [drafts, setDrafts] = useState<Record<string, ServiceDraft>>({})
    const [shopMarketOnly, setShopMarketOnly] = useState(false)
    const [mixEdits, setMixEdits] = useState<
        Record<string, GeneratedRFPMixGroup>
    >({})
    const [expandedServices, setExpandedServices] = useState<
        Record<string, boolean>
    >({})

    const sectionStyle: React.CSSProperties = {
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    }

    const sectionTitleStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle1),
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
    }

    const loadPacket = async (nextYear?: number) => {
        setLoading(true)
        try {
            const params: { year?: number; apartment_id?: number } = {}
            const yearParam = nextYear ?? initialYear
            if (yearParam) {
                params.year = yearParam
            }
            if (apartmentId) {
                params.apartment_id = apartmentId
            }
            const res = await axiosInstance.get<GeneratedRFPPacket>(
                'service-contract/rfp/generated-preview/',
                { params },
            )
            const data = res.data
            setPacket(data)
            setYear(data.year)
            setStartDate(toDateInput(data.project_start_date))
            setEndDate(toDateInput(data.project_end_date))
            setDeadline(toDateInput(data.submission_deadline))
            setShopMarketOnly(false)
            const nextDrafts: Record<string, ServiceDraft> = {}
            const nextExpanded: Record<string, boolean> = {}
            data.services.forEach((service) => {
                const vendorIds: Record<number, boolean> = {}
                serviceVendors(service).forEach((vendor) => {
                    vendorIds[vendor.id] = vendor.has_email
                })
                nextDrafts[service.key] = {
                    included:
                        Boolean(service.service_id) && !service.existing_rfp_id,
                    vendorIds,
                    includeCurrent: true,
                    insurance: service.insurance,
                    uploading: false,
                    notes: '',
                }
                nextExpanded[service.key] = false
            })
            setDrafts(nextDrafts)
            setExpandedServices(nextExpanded)
            const nextMix: Record<string, GeneratedRFPMixGroup> = {}
            ;(data.mix_groups || []).forEach((group) => {
                nextMix[mixGroupKey(group)] = { ...group }
            })
            setMixEdits(nextMix)
        } catch {
            toast.error('Unable to build the RFP preview.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!open) {
            setPacket(null)
            return
        }
        loadPacket()
    }, [open, apartmentId])

    const yearOptions = useMemo(() => {
        const base = year || new Date().getFullYear()
        return [base - 1, base, base + 1]
    }, [year])

    const mixGroups = useMemo(() => Object.values(mixEdits), [mixEdits])

    const setServiceDraft = (key: string, patch: Partial<ServiceDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [key]: { ...emptyDraft(), ...prev[key], ...patch },
        }))
    }

    const applyShopMarketOnly = (checked: boolean) => {
        setShopMarketOnly(checked)
        if (!packet) {
            return
        }
        setDrafts((prev) => {
            const next = { ...prev }
            packet.services.forEach((service) => {
                const draft = { ...emptyDraft(), ...next[service.key] }
                draft.includeCurrent = !checked
                const vendorIds = { ...draft.vendorIds }
                service.vendors.current.forEach((vendor) => {
                    vendorIds[vendor.id] = !checked && vendor.has_email
                })
                draft.vendorIds = vendorIds
                next[service.key] = draft
            })
            return next
        })
    }

    const toggleVendor = (
        service: GeneratedRFPServicePacket,
        vendor: GeneratedRFPVendor,
        checked: boolean,
    ) => {
        const draft = drafts[service.key] || emptyDraft()
        const vendorIds = { ...draft.vendorIds, [vendor.id]: checked }
        let includeCurrent = draft.includeCurrent
        if (vendor.invite_tier === BidInviteTier.CURRENT && !checked) {
            includeCurrent = false
        }
        setServiceDraft(service.key, { vendorIds, includeCurrent })
    }

    const setAllVendors = (
        service: GeneratedRFPServicePacket,
        checked: boolean,
    ) => {
        const draft = drafts[service.key] || emptyDraft()
        const vendorIds = { ...draft.vendorIds }
        serviceVendors(service).forEach((vendor) => {
            vendorIds[vendor.id] = checked && vendor.has_email
        })
        setServiceDraft(service.key, {
            vendorIds,
            includeCurrent: checked,
        })
        if (checked) {
            setShopMarketOnly(false)
        }
    }

    const uploadInsurance = async (
        service: GeneratedRFPServicePacket,
        file: File,
    ) => {
        if (!service.service_id) {
            return
        }
        setServiceDraft(service.key, { uploading: true })
        const body = new FormData()
        body.append('file', file)
        body.append('service_id', String(service.service_id))
        body.append('name', file.name)
        if (apartmentId) {
            body.append('apartment_id', String(apartmentId))
        }
        try {
            const res = await axiosInstance.post<GeneratedRFPInsurance>(
                'service-contract/rfp/generated-upload-sample-coi/',
                body,
            )
            setServiceDraft(service.key, {
                insurance: res.data,
                uploading: false,
            })
            toast.success(
                'Insurance file uploaded. It is now stored for this property.',
            )
        } catch {
            setServiceDraft(service.key, { uploading: false })
            toast.error('Unable to upload the insurance file.')
        }
    }

    const selectedVendorCount = (service: GeneratedRFPServicePacket) => {
        const draft = drafts[service.key]
        if (!draft) {
            return 0
        }
        return Object.values(draft.vendorIds).filter(Boolean).length
    }

    const vendorSelectionState = (service: GeneratedRFPServicePacket) => {
        const draft = drafts[service.key] || emptyDraft()
        const eligible = serviceVendors(service).filter(
            (vendor) => vendor.has_email,
        )
        const selected = eligible.filter((vendor) => draft.vendorIds[vendor.id])
        return {
            checked: eligible.length > 0 && selected.length === eligible.length,
            indeterminate:
                selected.length > 0 && selected.length < eligible.length,
            eligibleCount: eligible.length,
            selectedCount: selected.length,
        }
    }

    const canSend = useMemo(() => {
        if (!packet) {
            return false
        }
        return packet.services.some((service) => {
            const draft = drafts[service.key]
            return (
                draft?.included &&
                Boolean(service.service_id) &&
                !service.existing_rfp_id &&
                selectedVendorCount(service) > 0
            )
        })
    }, [packet, drafts])

    const send = async () => {
        if (!packet || year == null) {
            return
        }
        setSending(true)
        try {
            const services = packet.services
                .filter(
                    (service) =>
                        drafts[service.key]?.included && service.service_id,
                )
                .map((service) => {
                    const draft = drafts[service.key]
                    return {
                        service_id: service.service_id,
                        sample_coi_id: draft?.insurance?.id || null,
                        notes: (draft?.notes || '')
                            .trim()
                            .slice(0, SERVICE_NOTES_MAX),
                        vendor_ids: Object.entries(draft?.vendorIds || {})
                            .filter(([, checked]) => checked)
                            .map(([id]) => Number(id)),
                    }
                })
            const res = await axiosInstance.post(
                'service-contract/rfp/generated-send/',
                {
                    year,
                    apartment_id: apartmentId || undefined,
                    services,
                    project_start_date: startDate || null,
                    project_end_date: endDate || null,
                    submission_deadline: deadline || null,
                    mix_groups: mixGroups.map((group) => ({
                        bed_count: group.bed_count,
                        bath_count: group.bath_count,
                        full_count: group.full_count,
                        partial_count: group.partial_count,
                    })),
                },
            )
            const created = res.data.created?.length || 0
            const existing = res.data.existing?.length || 0
            if (created) {
                toast.success(
                    `Sent ${created} RFP${
                        created === 1 ? '' : 's'
                    }. Bids will show on the RFP page.`,
                )
            } else if (existing) {
                toast.info('Those RFPs were already sent for this year.')
            }
            if (res.data.errors?.length) {
                toast.error(res.data.errors[0])
            }
            if (onSent) {
                onSent()
            } else {
                onClose()
                history.push(dashboardPath || Routes.apartment.rfp.rfpDashboard)
            }
        } catch (err) {
            const axiosErr = err as AxiosError<{
                error?: string
                errors?: string[]
            }>
            const message =
                axiosErr.response?.data?.errors?.[0] ||
                axiosErr.response?.data?.error ||
                'Unable to send RFPs.'
            toast.error(message)
        } finally {
            setSending(false)
        }
    }

    const setMixCount = (
        group: GeneratedRFPMixGroup,
        field: 'full_count' | 'partial_count',
        raw: string,
    ) => {
        const key = mixGroupKey(group)
        setMixEdits((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]:
                    raw === ''
                        ? null
                        : Math.min(999, Math.max(0, parseInt(raw, 10) || 0)),
                source: 'property',
            },
        }))
    }

    const mixLines = useMemo(() => {
        if (mixGroups.length === 0) {
            return []
        }
        return mixGroups.map((group) => ({
            label: group.label,
            full: group.full_count == null ? 'TBD' : String(group.full_count),
            partial:
                group.partial_count == null
                    ? 'TBD'
                    : String(group.partial_count),
        }))
    }, [mixGroups])

    const topCluster = useMemo(() => {
        const rows = packet?.move_out_histogram || []
        if (rows.length === 0) {
            return null
        }
        return rows.reduce((best, row) => (row.count > best.count ? row : best))
    }, [packet])

    const renderVendorTable = (service: GeneratedRFPServicePacket) => {
        const draft = drafts[service.key] || emptyDraft()
        const vendors = serviceVendors(service)
        const selection = vendorSelectionState(service)
        const headerCell: React.CSSProperties = {
            ...typographyStyle(theme.typography.caption),
            fontWeight: 700,
            color: theme.palette.grey[700],
            textAlign: 'left',
            padding: '6px 8px',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            background: theme.palette.common.white,
            position: 'sticky',
            top: 0,
            zIndex: 1,
        }
        const cell: React.CSSProperties = {
            ...typographyStyle(theme.typography.body2),
            padding: '6px 8px',
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }

        if (vendors.length === 0) {
            return (
                <Typography variant="body2" color="textSecondary">
                    No vendors found for this service.
                </Typography>
            )
        }

        return (
            <Box
                style={{
                    overflow: 'auto',
                    maxHeight: 230,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 4,
                }}
            >
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ ...headerCell, width: 42 }}>
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    checked={selection.checked}
                                    indeterminate={selection.indeterminate}
                                    onChange={(event) =>
                                        setAllVendors(
                                            service,
                                            event.target.checked,
                                        )
                                    }
                                    inputProps={{
                                        'aria-label': 'Select all vendors',
                                    }}
                                />
                            </th>
                            <th style={headerCell}>Vendor</th>
                            <th
                                style={{
                                    ...headerCell,
                                    width: 44,
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                }}
                            >
                                <span
                                    style={{
                                        position: 'absolute',
                                        width: 1,
                                        height: 1,
                                        overflow: 'hidden',
                                    }}
                                >
                                    Badges
                                </span>
                            </th>
                            <th style={headerCell}>Location</th>
                            <th style={headerCell}>Distance</th>
                            <th style={headerCell}>Type</th>
                            <th style={headerCell}>Status</th>
                            <th style={headerCell}>Rating</th>
                            <th style={headerCell}>Go backs</th>
                            <th style={headerCell}>Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vendors.map((vendor) => {
                            const location = [vendor.city, vendor.state]
                                .filter(Boolean)
                                .join(', ')
                            const tier = (vendor.tier ??
                                'silver') as NVLVendorTier
                            const tierColor = tierColorFor(tier, theme)
                            const ratingValue = vendor.rating ?? 0
                            const hasRating = ratingValue > 0
                            const completeCount = vendor.complete_count ?? 0
                            return (
                                <tr key={vendor.id}>
                                    <td style={cell}>
                                        <Checkbox
                                            color="primary"
                                            size="small"
                                            checked={Boolean(
                                                draft.vendorIds[vendor.id],
                                            )}
                                            disabled={!vendor.has_email}
                                            onChange={(event) =>
                                                toggleVendor(
                                                    service,
                                                    vendor,
                                                    event.target.checked,
                                                )
                                            }
                                        />
                                    </td>
                                    <td style={cell}>
                                        {vendor.name}
                                        {!vendor.has_email ? ' (no email)' : ''}
                                    </td>
                                    <td
                                        style={{
                                            ...cell,
                                            width: 44,
                                            paddingLeft: 0,
                                            paddingRight: 4,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 4 }}
                                        >
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    width: 16,
                                                    height: 16,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {vendor.is_trained ? (
                                                    <Tooltip title="EZ Trained">
                                                        <span
                                                            style={{
                                                                display: 'flex',
                                                            }}
                                                        >
                                                            <RibbonThumbGreenIcon
                                                                style={{
                                                                    width: 16,
                                                                    height: 16,
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                ) : null}
                                            </span>
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    width: 16,
                                                    height: 16,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {vendor.will_travel ? (
                                                    <Tooltip title="Will Travel">
                                                        <span
                                                            style={{
                                                                display: 'flex',
                                                            }}
                                                        >
                                                            <RibbonAirplaneDarkIcon
                                                                style={{
                                                                    width: 16,
                                                                    height: 16,
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                ) : null}
                                            </span>
                                        </Box>
                                    </td>
                                    <td style={cell}>{location || '—'}</td>
                                    <td style={cell}>
                                        {vendor.distance_miles != null
                                            ? `${vendor.distance_miles} mi`
                                            : '—'}
                                    </td>
                                    <td style={cell}>
                                        {TIER_LABEL[vendor.invite_tier]}
                                    </td>
                                    <td style={cell}>
                                        <Tooltip
                                            title={<TierVolumeTooltip />}
                                            interactive
                                            placement="bottom-start"
                                        >
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: `${tierColor}22`,
                                                    border: `1px solid ${tierColor}`,
                                                    borderRadius: 16,
                                                    padding: '1px 8px',
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {NVL_TIER_LABELS[tier]}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td style={cell}>
                                        {hasRating ? (
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 4 }}
                                            >
                                                <Rating
                                                    name={`rfp-vendor-rating-${vendor.id}`}
                                                    value={ratingValue}
                                                    precision={0.5}
                                                    readOnly
                                                    size="small"
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color:
                                                            theme.palette
                                                                .grey[600],
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {ratingValue.toFixed(1)}
                                                </span>
                                            </Box>
                                        ) : (
                                            <span
                                                style={{
                                                    color:
                                                        theme.palette.grey[600],
                                                }}
                                            >
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td style={cell}>
                                        {vendor.go_back_rate != null
                                            ? `${vendor.go_back_rate.toFixed(
                                                  1,
                                              )}%`
                                            : '—'}
                                    </td>
                                    <td style={cell}>
                                        <Tooltip
                                            title={`${completeCount} completed work orders`}
                                        >
                                            <span>
                                                {formatCompleteCount(
                                                    completeCount,
                                                )}
                                            </span>
                                        </Tooltip>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </Box>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                style: {
                    maxWidth: 1060,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    background: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    padding: theme.spacing(2, 3, 2, 3),
                }}
            >
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <Box style={{ flex: 1 }}>
                        <Typography
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: 700,
                            }}
                        >
                            Request RFPs
                        </Typography>
                        {apartmentName || queueLabel ? (
                            <Typography
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                    marginTop: 2,
                                }}
                            >
                                {[apartmentName, queueLabel]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Typography>
                        ) : null}
                    </Box>
                    <Button onClick={onClose} style={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canSend || sending || loading}
                        onClick={send}
                        style={{ textTransform: 'none' }}
                    >
                        {sending ? 'Sending…' : 'Send RFPs'}
                    </Button>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                {!loading && packet && (
                    <>
                        <Box
                            display="flex"
                            style={{ gap: 10, marginTop: 14 }}
                            alignItems="stretch"
                        >
                            {[
                                {
                                    label: 'Window',
                                    value:
                                        startDate && endDate
                                            ? `${formatClusterDate(
                                                  startDate,
                                              )} – ${formatClusterDate(
                                                  endDate,
                                              )}`
                                            : '—',
                                },
                                {
                                    label: 'Bid by',
                                    value: deadline
                                        ? formatClusterDate(deadline)
                                        : '—',
                                },
                            ].map((card) => (
                                <Box
                                    key={card.label}
                                    style={{
                                        background: '#F7F8F8',
                                        border: '1px solid #E6E8E8',
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        flex: '0 0 auto',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: theme.palette.grey[600],
                                            fontWeight: 600,
                                            letterSpacing: 0.3,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {card.label}
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 15,
                                            lineHeight: 1.35,
                                        }}
                                    >
                                        {card.value}
                                    </Typography>
                                </Box>
                            ))}
                            <Box
                                display="flex"
                                alignItems="stretch"
                                flexWrap="wrap"
                                style={{
                                    gap: 10,
                                    marginLeft: 'auto',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {mixLines.length === 0 ? (
                                    <Box
                                        style={{
                                            background: '#F7F8F8',
                                            border: '1px solid #E6E8E8',
                                            borderRadius: 8,
                                            padding: '8px 12px',
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                            }}
                                        >
                                            No layouts
                                        </Typography>
                                    </Box>
                                ) : (
                                    mixLines.map((line) => (
                                        <Box
                                            key={line.label}
                                            style={{
                                                background: '#F7F8F8',
                                                border: '1px solid #E6E8E8',
                                                borderRadius: 8,
                                                padding: '8px 12px',
                                                minWidth: 92,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color:
                                                        theme.palette.grey[600],
                                                    fontWeight: 600,
                                                    letterSpacing: 0.3,
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {line.label}
                                            </Typography>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: 10,
                                                    marginTop: 2,
                                                }}
                                            >
                                                <Tooltip title="Full">
                                                    <span
                                                        style={{
                                                            display:
                                                                'inline-flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 3,
                                                        }}
                                                    >
                                                        <HomeIcon
                                                            style={{
                                                                fontSize: 16,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .grey[700],
                                                            }}
                                                        />
                                                        <Typography
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 15,
                                                            }}
                                                        >
                                                            {line.full}
                                                        </Typography>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Partial">
                                                    <span
                                                        style={{
                                                            display:
                                                                'inline-flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 3,
                                                        }}
                                                    >
                                                        <HomeOutlinedIcon
                                                            style={{
                                                                fontSize: 16,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .grey[700],
                                                            }}
                                                        />
                                                        <Typography
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 15,
                                                            }}
                                                        >
                                                            {line.partial}
                                                        </Typography>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>
                        <Box display="flex" style={{ gap: 10, marginTop: 10 }}>
                            {packet.services.map((service) => {
                                const draft =
                                    drafts[service.key] || emptyDraft()
                                const alreadySent = Boolean(
                                    service.existing_rfp_id,
                                )
                                const included = draft.included && !alreadySent
                                const selected = selectedVendorCount(service)
                                const serviceDetails = getServiceImageDetails(
                                    service.service_id ?? -1,
                                    service.service_category,
                                )
                                const accent =
                                    service.service_color ||
                                    serviceDetails.color ||
                                    theme.palette.primary.main
                                return (
                                    <Box
                                        key={service.key}
                                        flex={1}
                                        display="flex"
                                        alignItems="center"
                                        style={{
                                            gap: 10,
                                            opacity: included ? 1 : 0.45,
                                            border: `1px solid ${theme.palette.grey[200]}`,
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            background:
                                                theme.palette.common.white,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box
                                            style={{
                                                width: 6,
                                                alignSelf: 'stretch',
                                                backgroundColor: accent,
                                            }}
                                        />
                                        {serviceDetails.icon && (
                                            <img
                                                src={serviceDetails.icon}
                                                alt=""
                                                style={{
                                                    width: 22,
                                                    height: 22,
                                                }}
                                            />
                                        )}
                                        <Box flex={1} style={{ minWidth: 0 }}>
                                            <Typography
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                }}
                                            >
                                                {service.service_name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {selected} vendor
                                                {selected === 1 ? '' : 's'}
                                                {alreadySent
                                                    ? ' · already sent'
                                                    : ''}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                noWrap
                                                title={
                                                    draft.notes.trim() ||
                                                    undefined
                                                }
                                                style={{ display: 'block' }}
                                            >
                                                {draft.notes.trim()
                                                    ? `Notes: ${draft.notes.trim()}`
                                                    : 'Notes'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={
                                                draft.insurance
                                                    ? 'COI on file'
                                                    : 'No COI'
                                            }
                                            style={{
                                                marginRight: 10,
                                                height: 22,
                                                fontWeight: 600,
                                                backgroundColor: draft.insurance
                                                    ? '#E8F5E9'
                                                    : '#FFF3E0',
                                                color: draft.insurance
                                                    ? '#2E7D32'
                                                    : '#E65100',
                                            }}
                                        />
                                    </Box>
                                )
                            })}
                        </Box>
                    </>
                )}
            </Box>

            <DialogContent
                style={{
                    padding: theme.spacing(3),
                    overflowY: 'auto',
                }}
            >
                {loading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        padding={theme.spacing(4)}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!loading && packet && (
                    <>
                        <Box style={sectionStyle}>
                            <Typography style={sectionTitleStyle}>
                                Dates
                            </Typography>
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                style={{ gap: 20, flexWrap: 'nowrap' }}
                            >
                                {[
                                    {
                                        key: 'year',
                                        field: (
                                            <TextField
                                                select
                                                label="Turn year"
                                                value={year || ''}
                                                onChange={(event) =>
                                                    loadPacket(
                                                        Number(
                                                            event.target.value,
                                                        ),
                                                    )
                                                }
                                                style={{ width: 96 }}
                                            >
                                                {yearOptions.map((option) => (
                                                    <MenuItem
                                                        key={option}
                                                        value={option}
                                                    >
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ),
                                        help:
                                            'Changing year reloads units and vendors.',
                                    },
                                    {
                                        key: 'start',
                                        field: (
                                            <TextField
                                                label="Project start"
                                                type="date"
                                                value={startDate}
                                                onChange={(event) =>
                                                    setStartDate(
                                                        event.target.value,
                                                    )
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                style={{ width: 148 }}
                                            />
                                        ),
                                        help: topCluster
                                            ? `Largest move-out cluster is ${formatClusterDate(
                                                  topCluster.date,
                                              )} · ${topCluster.count} leases.`
                                            : 'First day vendors should plan to start.',
                                    },
                                    {
                                        key: 'end',
                                        field: (
                                            <TextField
                                                label="Project end"
                                                type="date"
                                                value={endDate}
                                                onChange={(event) =>
                                                    setEndDate(
                                                        event.target.value,
                                                    )
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                style={{ width: 148 }}
                                            />
                                        ),
                                        help:
                                            packet.project_end_source ===
                                            'move_in'
                                                ? packet.move_in_date
                                                    ? `Move-in cluster is ${formatClusterDate(
                                                          packet.move_in_date,
                                                      )}${
                                                          packet.move_in_count
                                                              ? ` · ${packet.move_in_count} leases`
                                                              : ''
                                                      }.`
                                                    : 'Based on the move-in cluster.'
                                                : packet.project_end_source ===
                                                  'prior_turn'
                                                ? `Last year's turn was ${
                                                      packet.prior_turn_days
                                                  } day${
                                                      packet.prior_turn_days ===
                                                      1
                                                          ? ''
                                                          : 's'
                                                  }.`
                                                : 'Defaulted to 3 weeks.',
                                    },
                                    {
                                        key: 'deadline',
                                        field: (
                                            <TextField
                                                label="Bid deadline"
                                                type="date"
                                                value={deadline}
                                                onChange={(event) =>
                                                    setDeadline(
                                                        event.target.value,
                                                    )
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                style={{ width: 148 }}
                                            />
                                        ),
                                        help:
                                            'When vendors must send pricing back. Defaulted to 14 days from today.',
                                    },
                                ].map((item) => (
                                    <Box key={item.key}>
                                        {item.field}
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            style={{
                                                display: 'block',
                                                marginTop: 4,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.help}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box style={sectionStyle}>
                            <Typography style={sectionTitleStyle}>
                                Units that need service
                            </Typography>
                            {packet.mix_source === 'tbd' ? (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ marginBottom: theme.spacing(1.5) }}
                                >
                                    No summer-turn history yet. Counts are TBD —
                                    vendors will still get the full pricing
                                    sheet. Add estimates if you want.
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ marginBottom: theme.spacing(1.5) }}
                                >
                                    Estimate from recent turns
                                    {packet.history_years?.length
                                        ? ` (${packet.history_years.join(
                                              ', ',
                                          )})`
                                        : ''}
                                    , not a guaranteed count. Edit if you have a
                                    better number.
                                </Typography>
                            )}
                            {mixGroups.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    No unit types are on the schedule for this
                                    property.
                                </Typography>
                            ) : (
                                mixGroups.map((group, index) => (
                                    <Box
                                        key={mixGroupKey(group)}
                                        style={{
                                            padding: '14px 0',
                                            borderBottom:
                                                index < mixGroups.length - 1
                                                    ? `1px solid ${theme.palette.grey[300]}`
                                                    : undefined,
                                        }}
                                    >
                                        <Typography
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: 8,
                                            }}
                                        >
                                            {group.label}
                                        </Typography>
                                        <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            style={{ gap: 16 }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="flex-end"
                                                style={{ gap: 6 }}
                                            >
                                                <Tooltip title="Full">
                                                    <HomeIcon
                                                        style={{
                                                            fontSize: 18,
                                                            color:
                                                                theme.palette
                                                                    .grey[700],
                                                            marginBottom: 8,
                                                        }}
                                                    />
                                                </Tooltip>
                                                <TextField
                                                    label="Full"
                                                    type="number"
                                                    size="small"
                                                    value={
                                                        group.full_count == null
                                                            ? ''
                                                            : group.full_count
                                                    }
                                                    onChange={(event) =>
                                                        setMixCount(
                                                            group,
                                                            'full_count',
                                                            event.target.value,
                                                        )
                                                    }
                                                    placeholder="TBD"
                                                    inputProps={{
                                                        min: 0,
                                                        max: 999,
                                                    }}
                                                    style={{ width: 64 }}
                                                />
                                            </Box>
                                            <Box
                                                display="flex"
                                                alignItems="flex-end"
                                                style={{ gap: 6 }}
                                            >
                                                <Tooltip title="Partial">
                                                    <HomeOutlinedIcon
                                                        style={{
                                                            fontSize: 18,
                                                            color:
                                                                theme.palette
                                                                    .grey[700],
                                                            marginBottom: 8,
                                                        }}
                                                    />
                                                </Tooltip>
                                                <TextField
                                                    label="Partial"
                                                    type="number"
                                                    size="small"
                                                    value={
                                                        group.partial_count ==
                                                        null
                                                            ? ''
                                                            : group.partial_count
                                                    }
                                                    onChange={(event) =>
                                                        setMixCount(
                                                            group,
                                                            'partial_count',
                                                            event.target.value,
                                                        )
                                                    }
                                                    placeholder="TBD"
                                                    inputProps={{
                                                        min: 0,
                                                        max: 999,
                                                    }}
                                                    style={{ width: 64 }}
                                                />
                                            </Box>
                                            <Box
                                                display="flex"
                                                flexWrap="wrap"
                                                alignItems="center"
                                                style={{
                                                    gap: 8,
                                                    flex: 1,
                                                    minWidth: 0,
                                                    paddingTop: 18,
                                                }}
                                            >
                                                {group.unit_types.map(
                                                    (unit) => {
                                                        const sqft = formatSqft(
                                                            unit,
                                                        )
                                                        const label = `${
                                                            unit.name
                                                        }${
                                                            sqft
                                                                ? ` · ${sqft}`
                                                                : ''
                                                        }`
                                                        const chip = (
                                                            <Box
                                                                style={{
                                                                    border: `1px solid ${theme.palette.grey[300]}`,
                                                                    borderRadius: 6,
                                                                    padding:
                                                                        '4px 8px',
                                                                    background:
                                                                        theme
                                                                            .palette
                                                                            .common
                                                                            .white,
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    color:
                                                                        theme
                                                                            .palette
                                                                            .grey[800],
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {label}
                                                            </Box>
                                                        )
                                                        if (
                                                            !unit.floorplan_image
                                                        ) {
                                                            return (
                                                                <Tooltip
                                                                    key={
                                                                        unit.unit_config_id
                                                                    }
                                                                    title="No image available"
                                                                >
                                                                    <span>
                                                                        {chip}
                                                                    </span>
                                                                </Tooltip>
                                                            )
                                                        }
                                                        return (
                                                            <Tooltip
                                                                key={
                                                                    unit.unit_config_id
                                                                }
                                                                title={
                                                                    <img
                                                                        src={
                                                                            unit.floorplan_image
                                                                        }
                                                                        alt=""
                                                                        style={{
                                                                            width: 220,
                                                                            display:
                                                                                'block',
                                                                        }}
                                                                    />
                                                                }
                                                            >
                                                                <span>
                                                                    <FloorplanPreview
                                                                        src={
                                                                            unit.floorplan_image
                                                                        }
                                                                        alt={`${unit.name} floor plan`}
                                                                    >
                                                                        {chip}
                                                                    </FloorplanPreview>
                                                                </span>
                                                            </Tooltip>
                                                        )
                                                    },
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>

                        <Box style={sectionStyle}>
                            <Typography style={sectionTitleStyle}>
                                Services
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={shopMarketOnly}
                                        onChange={(event) =>
                                            applyShopMarketOnly(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="Shop the market only (do not include current vendors)"
                            />

                            {packet.services.map((service) => {
                                const draft =
                                    drafts[service.key] || emptyDraft()
                                const alreadySent = Boolean(
                                    service.existing_rfp_id,
                                )
                                const serviceDetails = getServiceImageDetails(
                                    service.service_id ?? -1,
                                    service.service_category,
                                )
                                const accent =
                                    service.service_color ||
                                    serviceDetails.color ||
                                    theme.palette.primary.main
                                const expanded = Boolean(
                                    expandedServices[service.key],
                                )
                                const selection = vendorSelectionState(service)
                                return (
                                    <Box
                                        key={service.key}
                                        marginTop={2}
                                        style={{
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderLeft: `6px solid ${accent}`,
                                            borderRadius: 8,
                                            padding: theme.spacing(1.5, 2),
                                            backgroundColor:
                                                accent.startsWith('#') &&
                                                accent.length === 7
                                                    ? `${accent}14`
                                                    : theme.palette.grey[50],
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8 }}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={draft.included}
                                                disabled={
                                                    !service.service_id ||
                                                    alreadySent
                                                }
                                                onChange={(event) =>
                                                    setServiceDraft(
                                                        service.key,
                                                        {
                                                            included:
                                                                event.target
                                                                    .checked,
                                                        },
                                                    )
                                                }
                                            />
                                            {serviceDetails.icon && (
                                                <img
                                                    src={serviceDetails.icon}
                                                    alt=""
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                />
                                            )}
                                            <Box
                                                flex={1}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    setExpandedServices(
                                                        (prev) => ({
                                                            ...prev,
                                                            [service.key]: !expanded,
                                                        }),
                                                    )
                                                }
                                            >
                                                <Typography
                                                    style={{ fontWeight: 700 }}
                                                >
                                                    {service.service_name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {selection.selectedCount} of{' '}
                                                    {selection.eligibleCount}{' '}
                                                    vendors selected
                                                </Typography>
                                            </Box>
                                            <Chip
                                                size="small"
                                                label={
                                                    draft.insurance
                                                        ? `COI: ${draft.insurance.name}`
                                                        : 'No insurance'
                                                }
                                                style={{
                                                    backgroundColor: draft.insurance
                                                        ? '#E8F5E9'
                                                        : '#FFF3E0',
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setExpandedServices(
                                                        (prev) => ({
                                                            ...prev,
                                                            [service.key]: !expanded,
                                                        }),
                                                    )
                                                }
                                            >
                                                {expanded ? (
                                                    <ExpandLessIcon />
                                                ) : (
                                                    <ExpandMoreIcon />
                                                )}
                                            </IconButton>
                                        </Box>
                                        <Collapse in={expanded}>
                                            {alreadySent && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Already sent for {year}.
                                                    Open it on the RFP page.
                                                </Typography>
                                            )}
                                            {!service.service_id && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    No {service.key} service is
                                                    configured for this
                                                    property.
                                                </Typography>
                                            )}
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: 12,
                                                    marginTop: 12,
                                                    marginBottom: 12,
                                                }}
                                            >
                                                {draft.insurance?.file ? (
                                                    <Link
                                                        href={
                                                            draft.insurance.file
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {draft.insurance.name}
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2">
                                                        Optional. Vendors see
                                                        this if you upload a
                                                        sample COI for{' '}
                                                        {service.service_name}.
                                                    </Typography>
                                                )}
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        <CloudUploadIcon />
                                                    }
                                                    disabled={
                                                        draft.uploading ||
                                                        !service.service_id
                                                    }
                                                    style={{
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    {draft.uploading
                                                        ? 'Uploading…'
                                                        : draft.insurance
                                                        ? 'Replace insurance'
                                                        : 'Upload insurance'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="application/pdf,image/*"
                                                        onChange={(event) => {
                                                            const file =
                                                                event.target
                                                                    .files?.[0]
                                                            if (file) {
                                                                uploadInsurance(
                                                                    service,
                                                                    file,
                                                                )
                                                            }
                                                            event.target.value =
                                                                ''
                                                        }}
                                                    />
                                                </Button>
                                            </Box>
                                            <TextField
                                                label="Notes"
                                                placeholder="e.g. We'll supply the paint"
                                                value={draft.notes}
                                                onChange={(event) =>
                                                    setServiceDraft(
                                                        service.key,
                                                        {
                                                            notes: event.target.value.slice(
                                                                0,
                                                                SERVICE_NOTES_MAX,
                                                            ),
                                                        },
                                                    )
                                                }
                                                multiline
                                                rows={2}
                                                fullWidth
                                                disabled={
                                                    alreadySent ||
                                                    !service.service_id
                                                }
                                                helperText={`${draft.notes.length}/${SERVICE_NOTES_MAX}. Optional. Vendors see this.`}
                                                style={{ marginBottom: 12 }}
                                            />
                                            {!service.vendors.has_origin && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    Set the property latitude
                                                    and longitude to include
                                                    nearby and will-travel
                                                    vendors.
                                                </Typography>
                                            )}
                                            {renderVendorTable(service)}
                                        </Collapse>
                                    </Box>
                                )
                            })}
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
